import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Input, Select, Space, Card, Tooltip } from 'antd';
import { useSearchParams, Link } from 'react-router-dom';
import { formatDateTime } from '../../utils/format';
import { getTransfers } from '../../services/transfers';

const { Search } = Input;
const { Option } = Select;

const TransferList = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [searchParams, setSearchParams] = useSearchParams();

  const filtersFromUrl = useMemo(() => {
    const f = {};
    for (const [key, value] of searchParams.entries()) {
      f[key] = value;
    }
    return f;
  }, [searchParams]);

  const fetchData = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await getTransfers({
        page: params.current || 1,
        page_size: params.pageSize || 20,
        ...filtersFromUrl,
      });
      setData(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.meta.total,
        current: response.meta.page,
        pageSize: response.meta.page_size,
      }));
    } catch (error) {
      console.error('获取转账记录失败:', error);
    }
    setLoading(false);
  }, [filtersFromUrl]);

  const { current, pageSize } = pagination;
  useEffect(() => {
    fetchData({
      current,
      pageSize,
    });
  }, [fetchData, current, pageSize]);

  const handleTableChange = (newPagination) => {
    fetchData({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const handleSearch = (field, value) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(field, value);
      } else {
        next.delete(field);
      }
      return next;
    }, { replace: true });
    setPagination(prev => ({
      ...prev,
      current: 1,
    }));
  };

  const stateMap = {
    0: { cn: '等待中', en: 'Pending' },
    1: { cn: '处理中', en: 'Processing' },
    2: { cn: '已完成', en: 'Completed' },
    3: { cn: '失败', en: 'Failed' },
    4: { cn: '已取消', en: 'Cancelled' },
  };

  // 添加文本截断的工具函数
  const truncateText = (text) => {
    if (!text || text.length <= 10) return text;
    return `${text.substr(0, 5)}...${text.substr(-5)}`;
  };

  const renderWithTooltip = (text) => (
    <Tooltip placement="topLeft" title={text} copyable>
      {truncateText(text)}
    </Tooltip>
  );

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'GUID',
      dataIndex: 'guid',
      key: 'guid',
      width: 140,
      ellipsis: false,
      render: renderWithTooltip,
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (amount, record) => (
        <>
          {parseFloat(amount)}{' '}
          {record.asset_id ? (
            <Link to={`/dashboard/explorer/asset/${record.asset_id}`}>{record.symbol}</Link>
          ) : record.symbol}
        </>
      ), 
    },
    {
      title: '状态',
      dataIndex: 'state',
      key: 'state',
      width: 100,
      render: (state) => stateMap[state]?.cn || state,
    },
    {
      title: '地址',
      dataIndex: 'address',
      key: 'address',
      width: 140,
      ellipsis: false,
      render: renderWithTooltip,
    },
    {
      title: '交易ID',
      dataIndex: 'txid',
      key: 'txid',
      width: 140,
      ellipsis: false,
      render: (text) => text ? (
        <Tooltip placement="topLeft" title={text} copyable>
          <Link to={`/dashboard/explorer/transaction/${text}`} style={{ fontFamily: 'monospace' }}>
            {truncateText(text)}
          </Link>
        </Tooltip>
      ) : '-',
    },
    {
      title: '链',
      dataIndex: 'chain',
      key: 'chain',
      width: 100,
    },
    {
      title: '资产',
      dataIndex: 'asset',
      key: 'asset',
      width: 100,
      render: (text, record) => record.asset_id ? (
        <Link to={`/dashboard/explorer/asset/${record.asset_id}`}>{text}</Link>
      ) : text,
    },
    {
      title: '备注',
      dataIndex: 'memo',
      key: 'memo',
      width: 140,
      ellipsis: false,
      render: renderWithTooltip,
    },
    {
      title: '消息',
      dataIndex: 'message',
      key: 'message',
      width: 140,
    },
    {
      title: '创建时间',
      dataIndex: 'inserted_at',
      key: 'inserted_at',
      width: 180,
      render: (d) => formatDateTime(d),
    },
  ];

  return (
    <Card title="转账记录">
      <Space style={{ marginBottom: 16 }}>
        <Search
          placeholder="搜索地址"
          defaultValue={filtersFromUrl.address || ''}
          onSearch={(value) => handleSearch('address', value)}
          style={{ width: 200 }}
        />
        <Search
          placeholder="搜索交易ID"
          defaultValue={filtersFromUrl.txid || ''}
          onSearch={(value) => handleSearch('txid', value)}
          style={{ width: 200 }}
        />
        <Search
          placeholder="搜索链名称"
          defaultValue={filtersFromUrl.chain || ''}
          onSearch={(value) => handleSearch('chain', value)}
          style={{ width: 200 }}
        />
        <Search
          placeholder="搜索资产名称"
          defaultValue={filtersFromUrl.asset || ''}
          onSearch={(value) => handleSearch('asset', value)}
          style={{ width: 200 }}
        />
        <Select
          mode="multiple"
          placeholder="选择状态"
          allowClear
          style={{ minWidth: 240 }}
          value={filtersFromUrl.state?.split(',').filter(Boolean) || []}
          onChange={(value) => handleSearch('state', value.join(','))}
        >
          {Object.entries(stateMap).map(([key, { cn, en }]) => (
            <Option key={key} value={key}>{cn} ({en})</Option>
          ))}
        </Select>
      </Space>
      <Table
        columns={columns}
        dataSource={data}
        pagination={pagination}
        loading={loading}
        onChange={handleTableChange}
        scroll={{ x: 700 }}
        rowKey="id"
      />
    </Card>
  );
};

export default TransferList;
